import { useState } from 'react';
import { Plus, User, Phone, Mail, FileText, Pencil, Trash2, MapPin } from 'lucide-react';
import { Button, Modal, Field, inputClass } from '@/components/ui';
import { useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { formatDate, cn } from '@/lib/utils';
import type { StoreAssociate, StoreLocation } from './storeTypes';

interface AssociateForm {
  name: string;
  email: string;
  phone: string;
  notes: string;
  startDate: string;
  endDate: string;
  locationId: string;
}

const EMPTY: AssociateForm = {
  name: '',
  email: '',
  phone: '',
  notes: '',
  startDate: '',
  endDate: '',
  locationId: '',
};

function toForm(sa: StoreAssociate): AssociateForm {
  return {
    name: sa.name,
    email: sa.email ?? '',
    phone: sa.phone ?? '',
    notes: sa.notes ?? '',
    startDate: sa.startDate ?? '',
    endDate: sa.endDate ?? '',
    locationId: sa.locationId ?? '',
  };
}

export function StoreAssociates({
  storeId,
  associates,
  locations,
  onChanged,
}: {
  storeId: string;
  associates: StoreAssociate[];
  locations: StoreLocation[];
  onChanged: () => void;
}) {
  const { mutate, loading: saving } = useMutation();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StoreAssociate | null>(null);
  const [form, setForm] = useState<AssociateForm>(EMPTY);

  const active = associates.filter((a) => !a.isArchived);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setAdding(true);
  };

  const openEdit = (sa: StoreAssociate) => {
    setEditing(sa);
    setForm(toForm(sa));
    setAdding(true);
  };

  const close = () => {
    setAdding(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const body = {
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      notes: form.notes.trim() || undefined,
      startDate: form.startDate.trim() || undefined,
      endDate: form.endDate.trim() || undefined,
      locationId: form.locationId || undefined,
    };
    let ok = false;
    if (editing) {
      // PATCH must use api.patch directly (useMutation has no patch).
      try {
        await api.patch(`/associates/${editing.id}`, body);
        ok = true;
      } catch {
        ok = false;
      }
    } else {
      const res = await mutate('post', `/stores/${storeId}/associates`, body);
      ok = res !== null;
    }
    if (ok) {
      close();
      onChanged();
    }
  };

  const handleDelete = async (id: string) => {
    const res = await mutate('delete', `/associates/${id}`);
    if (res !== null) onChanged();
  };

  const locationName = (id: string | null): string | null => {
    if (!id) return null;
    const loc = locations.find((l) => l.id === id);
    if (!loc) return null;
    return loc.name?.trim() || loc.address?.trim() || 'Location';
  };

  return (
    <div className="border-t border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Sales Associates
        </h4>
        <button
          onClick={openAdd}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Plus className="w-3.5 h-3.5" />
          Add SA
        </button>
      </div>

      {active.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-2">No sales associates added</p>
      ) : (
        <div className="space-y-2">
          {active.map((sa) => {
            const locLabel = locationName(sa.locationId);
            return (
              <div key={sa.id} className="bg-secondary/50 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <p className="flex-1 text-sm font-medium text-foreground">{sa.name}</p>
                  <button
                    onClick={() => openEdit(sa)}
                    className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground"
                    aria-label="Edit associate"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(sa.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                    aria-label="Delete associate"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2 space-y-1 ml-10">
                  {locLabel && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3" />
                      {locLabel}
                    </div>
                  )}
                  {sa.phone && (
                    <a
                      href={`tel:${sa.phone}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
                    >
                      <Phone className="w-3 h-3" />
                      {sa.phone}
                    </a>
                  )}
                  {sa.email && (
                    <a
                      href={`mailto:${sa.email}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
                    >
                      <Mail className="w-3 h-3" />
                      {sa.email}
                    </a>
                  )}
                  {sa.notes && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <FileText className="w-3 h-3 mt-0.5" />
                      <span>{sa.notes}</span>
                    </div>
                  )}
                  {(sa.startDate || sa.endDate) && (
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {sa.startDate && <span>Since {formatDate(sa.startDate)}</span>}
                      {sa.endDate && <span>Until {formatDate(sa.endDate)}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={adding}
        onClose={close}
        title={editing ? 'Edit Sales Associate' : 'Add Sales Associate'}
        footer={
          <Button fullWidth onClick={handleSave} disabled={!form.name.trim() || saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Sales Associate'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., John Smith"
              className={inputClass}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g., +1 212-555-0123"
              className={inputClass}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="e.g., john@store.com"
              className={inputClass}
            />
          </Field>
          {locations.length > 0 && (
            <Field label="Location">
              <select
                value={form.locationId}
                onChange={(e) => setForm({ ...form, locationId: e.target.value })}
                className={inputClass}
              >
                <option value="">— No specific location —</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name?.trim() || loc.address?.trim() || 'Location'}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Since">
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Until">
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any notes about this associate…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
